/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
 */

import { type TalerMerchantApi } from "@gnu-taler/taler-util";
import { merchantManagementClient, type BackendConfig } from "./client.js";
import { unwrap } from "./failure.js";

export type ExchangeStatus = TalerMerchantApi.ExchangeStatusDetail;
export const EXCHANGE_STATUS_MAX_DELAY = 5 * 60 * 1000;

export async function readExchangeStatus(
  config: BackendConfig,
): Promise<ExchangeStatus[]> {
  return unwrap(await merchantManagementClient(config).listExchanges())
    .exchanges;
}

/** Leave the updater five seconds to finish, and never spin on an overdue date. */
export function exchangeStatusRefreshDelay(
  statuses: ExchangeStatus[] | undefined,
  now = Date.now(),
): number {
  let delay = EXCHANGE_STATUS_MAX_DELAY;
  for (const status of statuses ?? []) {
    const seconds = status.next_download.t_s;
    if (typeof seconds === "number" && Number.isFinite(seconds)) {
      delay = Math.min(delay, Math.max(5000, seconds * 1000 + 5000 - now));
    }
  }
  return delay;
}

/** One request at a time, including manual refresh and focus/reconnect events. */
export function createExchangeStatusPoller(
  request: () => Promise<ExchangeStatus[] | undefined>,
) {
  let disposed = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let pending: Promise<void> | undefined;

  function refresh(): Promise<void> {
    if (disposed) return Promise.resolve();
    if (pending) return pending;
    clearTimeout(timer);
    pending = Promise.resolve().then(async () => {
      if (disposed) {
        pending = undefined;
        return;
      }
      let delay = EXCHANGE_STATUS_MAX_DELAY;
      try {
        delay = exchangeStatusRefreshDelay(await request());
      } catch {
        // The resource owns the error display. Keep retrying even after failures.
      } finally {
        pending = undefined;
        if (!disposed) timer = setTimeout(() => void refresh(), delay);
      }
    });
    return pending;
  }

  return {
    refresh,
    dispose() {
      disposed = true;
      clearTimeout(timer);
    },
  };
}
